import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { Table, SectionTitle } from "../../../shared";
import PersonaAction from "./action";
import { ALL_PERSONA_GROUPS } from "./query";
import { AxiosGraphQlAction, Helper } from "../../../services";
import { setPersonaGroupList, getPersonaGroupList } from "../../reducers";

const columns = [
    {
        accessorKey: "personaName",
        header: "Persona Name",
    },
    {
        accessorKey: "appName",
        header: "Application",
        cell: (cell: any) => Helper.GetAppName(cell.getValue()),
    },
    {
        accessorKey: "country",
        header: "Country",
        cell: (cell: any) => Helper.GetCountryName(cell.getValue()),
    },
];

const renderSubComponent = (data: any) => {
    return <PersonaAction data={data.row.original} />;
};

const PersonaGroup = (props: any) => {
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState(
        props.countryList.length
            ? {
                  personaType: {
                      equalTo: "Group",
                  },
                  country: {
                      in: [...props.countryList],
                  },
              }
            : {
                  personaType: {
                      equalTo: "Group",
                  },
              }
    );

    const [{ pageIndex, pageSize }, setPagination] = React.useState({
        pageIndex: 0,
        pageSize: 10,
    });

    const pagination = React.useMemo(
        () => ({
            pageIndex,
            pageSize,
        }),
        [pageIndex, pageSize]
    );

    const [tableInfo, setTableInfo] = useState({
        pageIndex,
        pageSize,
        totalCount: 0,
        pageInfo: {
            hasNextPage: undefined,
            hasPreviousPage: undefined,
            startCursor: undefined,
            endCursor: undefined,
        },
    });

    useEffect((): any => {
        setLoading(true);
        let isApiCalled = true;
        if (isApiCalled) {
            const { startCursor, endCursor } = tableInfo.pageInfo;
            let variables = {};

            if (pageIndex > tableInfo.pageIndex) {
                variables = {
                    filter: {
                        ...filter,
                    },
                    last: undefined,
                    before: undefined,
                    first: endCursor ? pageSize : undefined,
                    after: endCursor,
                };
            } else if (pageIndex < tableInfo.pageIndex) {
                variables = {
                    filter: {
                        ...filter,
                    },
                    last: startCursor ? pageSize : undefined,
                    before: startCursor,
                    first: startCursor ? undefined : pageSize,
                    after: undefined,
                };
            } else {
                variables = {
                    filter: {
                        ...filter,
                    },
                    first: pageSize,
                    after: undefined,
                    last: undefined,
                    before: undefined,
                };
            }

            AxiosGraphQlAction(variables, ALL_PERSONA_GROUPS)
                .then(({ data }) => {
                    const personaGroupList = data.data.allAbbvieProPersonas.nodes;
                    const pageInfo = data.data.allAbbvieProPersonas.pageInfo;
                    const totalCount = data.data.allAbbvieProPersonas.totalCount;

                    setTableInfo({
                        pageIndex,
                        pageSize,
                        totalCount,
                        pageInfo: { ...pageInfo },
                    });

                    setLoading(false);
                    props.setPersonaList(personaGroupList);
                })
                .catch((err) => {
                    setLoading(false);
                    toast.error(err.message);
                });
        }
        return () => {
            props.setPersonaList([]);
            isApiCalled = false;
        };
    }, [pageIndex, pageSize, filter]);

    const handleChange = (value: string, columnName: string) => {
        const filterType =
            columnName === "appName"
                ? "equalTo"
                : columnName === "country"
                  ? "in"
                  : "includesInsensitive";

        const defaultValue = columnName === "country" ? [...props.countryList] : "";

        if (value) {
            setFilter({
                ...filter,
                [columnName]: {
                    [filterType]: value,
                },
            });
        } else {
            if (columnName === "appName") {
                // @ts-expect-error: Object is possibly 'undefined'.
                delete filter?.appName;
                setFilter({
                    ...filter,
                });
            } else if (columnName === "country" && props.countryList.length === 0) {
                delete filter?.country;
                setFilter({
                    ...filter,
                });
            } else {
                setFilter({
                    ...filter,
                    [columnName]: {
                        [filterType]: defaultValue,
                    },
                });
            }
        }
    };

    const pageCount = Math.ceil(tableInfo.totalCount / pageSize);

    const pageSizeChangeAction = () => {
        setTableInfo({
            ...tableInfo,
            pageInfo: {
                hasNextPage: undefined,
                hasPreviousPage: undefined,
                startCursor: undefined,
                endCursor: undefined,
            },
        });
    };

    return (
        <>
            <SectionTitle
                title="Persona Group"
                buttonText="Persona Group"
                buttonUrl="/abbvie-pro/persona/group/create"
                backUrl="/abbvie-pro/persona"
            />

            <Table
                data={props.personaGroup}
                headers={columns}
                getRowCanExpand={() => true}
                renderSubComponent={renderSubComponent}
                loading={loading}
                pageCount={pageCount}
                totalCount={tableInfo.totalCount}
                pagination={pagination}
                onPaginationChange={setPagination}
                handleFilter={(value: string, columnName: string) => {
                    handleChange(value, columnName);
                }}
                pageSizeChangeAction={() => {
                    pageSizeChangeAction();
                }}
            />
        </>
    );
};

const mapStateToProps = (state: any) => {
    return {
        personaGroup: getPersonaGroupList(state),
        countryList: state.users.currentUser.userType === 1 ? state.users.currentUser.country : [],
    };
};
const mapDispatchToProps = (dispatch: any) => {
    return {
        setPersonaList: (personaList: any) => {
            dispatch(setPersonaGroupList(personaList));
        },
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(PersonaGroup);
